import React, { useEffect, useMemo, useState } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import {
  Crown, Shield, Users, X, Search, UserPlus, ChevronRight, ShieldCheck,
} from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

const EXEC_ROLES = ["President", "Vice President", "Secretary", "Treasurer"];
const COACH_LEVELS = ["Coach", "Assistant Coach"];
const COACH_TEAMS = ["Men", "Women", "Junior Men", "Junior Women"];

function fullName(u) {
  if (!u) return "";
  return `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || u.member_id || "";
}

/** Compact pill for one user (used inside slots/lists). */
function PersonPill({ user, onRemove, testid }) {
  if (!user) return null;
  return (
    <div className="inline-flex items-center gap-1.5 pl-1 pr-2 py-1 rounded-full bg-[hsl(var(--secondary))] border border-border" data-testid={testid}>
      <div className="h-6 w-6 rounded-full bg-[hsl(var(--fg))] text-[hsl(var(--bg))] flex items-center justify-center text-[10px] font-semibold">
        {(user.first_name?.[0] || "?").toUpperCase()}
      </div>
      <div className="text-xs">
        <span className="font-medium">{fullName(user)}</span>
        <span className="text-muted-foreground ml-1">{user.member_id}</span>
      </div>
      {onRemove && (
        <button onClick={onRemove} aria-label="Remove" className="ml-0.5 text-muted-foreground hover:text-destructive">
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

/**
 * Picker dialog — choose ONE member from the list of approved members.
 * Used for executive roles (each role has a single holder).
 */
function MemberPickerDialog({ open, onOpenChange, members, currentUserId, title, description, onPick }) {
  const [q, setQ] = useState("");
  useEffect(() => { if (!open) setQ(""); }, [open]);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return members;
    return members.filter((m) => `${m.first_name} ${m.last_name} ${m.email} ${m.member_id}`.toLowerCase().includes(t));
  }, [q, members]);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 pl-9 rounded-lg"
            placeholder="Search members…"
            data-testid="role-picker-search"
            autoFocus
          />
        </div>
        <div className="max-h-72 overflow-y-auto -mx-2 mt-2">
          {filtered.length === 0 && (
            <p className="text-xs text-muted-foreground text-center py-6">No members found.</p>
          )}
          {filtered.map((m) => {
            const isCurrent = m.id === currentUserId;
            return (
              <button
                key={m.id}
                onClick={() => { onPick(m); onOpenChange(false); }}
                className="w-full text-left px-3 py-2 rounded-lg hover:bg-[hsl(var(--secondary))] flex items-center gap-3 group"
                data-testid={`role-picker-item-${m.member_id}`}
              >
                <div className="h-8 w-8 rounded-full bg-[hsl(var(--fg))] text-[hsl(var(--bg))] flex items-center justify-center text-xs font-semibold shrink-0">
                  {(m.first_name?.[0] || "?").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{fullName(m)}</div>
                  <div className="text-[11px] text-muted-foreground">
                    {m.member_id} • {m.membership_type || "—"}
                    {m.position && <span className="ml-1">• {m.position}</span>}
                  </div>
                </div>
                {isCurrent && <Badge variant="secondary" className="text-[10px]">Current</Badge>}
                <ChevronRight className="h-4 w-4 text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Multi-select picker — toggle multiple members in or out (used for General
 * Committee and Coaching positions).
 */
function MemberMultiPickerDialog({ open, onOpenChange, members, selectedIds, title, description, onSave }) {
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState(new Set());
  useEffect(() => {
    if (open) {
      setDraft(new Set(selectedIds || []));
      setQ("");
    }
  }, [open, selectedIds]);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return members;
    return members.filter((m) => `${m.first_name} ${m.last_name} ${m.email} ${m.member_id}`.toLowerCase().includes(t));
  }, [q, members]);
  const toggle = (id) => {
    setDraft((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 pl-9 rounded-lg"
            placeholder="Search members…"
            autoFocus
          />
        </div>
        <div className="text-[11px] text-muted-foreground mt-1">{draft.size} selected</div>
        <div className="max-h-72 overflow-y-auto -mx-2 mt-1 space-y-0.5">
          {filtered.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No members found.</p>}
          {filtered.map((m) => (
            <label
              key={m.id}
              className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[hsl(var(--secondary))] cursor-pointer"
              data-testid={`role-multi-item-${m.member_id}`}
            >
              <Checkbox checked={draft.has(m.id)} onCheckedChange={() => toggle(m.id)} />
              <div className="h-8 w-8 rounded-full bg-[hsl(var(--fg))] text-[hsl(var(--bg))] flex items-center justify-center text-xs font-semibold">
                {(m.first_name?.[0] || "?").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{fullName(m)}</div>
                <div className="text-[11px] text-muted-foreground">{m.member_id} • {m.membership_type || "—"}</div>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => { onSave([...draft]); onOpenChange(false); }} data-testid="role-multi-save">
            Save ({draft.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/**
 * Admin multi-picker dialog — same UX as MemberMultiPickerDialog but
 * sourced from the admin list (no member_id / membership_type). Used
 * by the Member Managers card so admins can be picked in a single
 * dialog instead of clicking each switch.
 */
function AdminMultiPickerDialog({ open, onOpenChange, admins, selectedIds, title, description, onSave }) {
  const [q, setQ] = useState("");
  const [draft, setDraft] = useState(new Set(selectedIds));
  useEffect(() => {
    if (open) {
      setDraft(new Set(selectedIds));
      setQ("");
    }
  }, [open, selectedIds]);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    if (!t) return admins;
    return admins.filter((a) => `${a.first_name} ${a.last_name} ${a.email} ${a.username}`.toLowerCase().includes(t));
  }, [q, admins]);
  const toggle = (id) => {
    setDraft((s) => {
      const n = new Set(s);
      if (n.has(id)) n.delete(id); else n.add(id);
      return n;
    });
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <div className="relative">
          <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="h-9 pl-9 rounded-lg"
            placeholder="Search admins…"
            autoFocus
          />
        </div>
        <div className="text-[11px] text-muted-foreground mt-1">{draft.size} selected</div>
        <div className="max-h-72 overflow-y-auto -mx-2 mt-1 space-y-0.5">
          {filtered.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No admins found.</p>}
          {filtered.map((a) => (
            <label
              key={a.id}
              className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-[hsl(var(--secondary))] cursor-pointer"
              data-testid={`admin-multi-item-${a.id}`}
            >
              <Checkbox checked={draft.has(a.id)} onCheckedChange={() => toggle(a.id)} />
              <div className="h-8 w-8 rounded-full bg-[hsl(var(--fg))] text-[hsl(var(--bg))] flex items-center justify-center text-xs font-semibold">
                {(a.first_name?.[0] || a.username?.[0] || "?").toUpperCase()}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">
                  {`${a.first_name || ""} ${a.last_name || ""}`.trim() || a.username || a.email}
                  {a.role === "primary_admin" && (
                    <Badge variant="outline" className="ml-2 rounded-full text-[10px] px-1.5 py-0 border-[hsl(var(--accent))]/40 text-[hsl(var(--accent))]">Primary</Badge>
                  )}
                </div>
                <div className="text-[11px] text-muted-foreground truncate">{a.email || a.username}</div>
              </div>
            </label>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => { onSave([...draft]); onOpenChange(false); }} data-testid="admin-multi-save">
            Save ({draft.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


export default function RolesPanel({ onDataChange }) {
  const [summary, setSummary] = useState(null);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Member-manager admins — surfaced here so the "who gets the new
  // PlayHQ link nudge" toggle sits alongside the other club roles.
  const [managers, setManagers] = useState([]);

  // Picker state
  const [execPicker, setExecPicker] = useState(null);    // { role: "President" } | null
  const [commPickerOpen, setCommPickerOpen] = useState(false);
  const [coachPicker, setCoachPicker] = useState(null);  // { slot: "Coach - Men" } | null
  const [managerPickerOpen, setManagerPickerOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [s, m, mgr] = await Promise.all([
        api.get("/admin/roles/summary"),
        api.get("/members"),
        api.get("/admin/member-managers").catch(() => ({ data: [] })),
      ]);
      setSummary(s.data);
      setMembers(m.data || []);
      setManagers(mgr.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load roles");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const toggleManager = async (user_id, next) => {
    // Optimistic — flip the switch, revert on failure.
    setManagers((prev) => prev.map((r) => r.id === user_id ? { ...r, is_member_manager: next } : r));
    try {
      await api.put(`/admin/member-managers/${user_id}`, { is_member_manager: next });
      toast.success(next ? "Now a member manager" : "Manager role removed");
      onDataChange?.();
    } catch (e) {
      setManagers((prev) => prev.map((r) => r.id === user_id ? { ...r, is_member_manager: !next } : r));
      toast.error("Could not update manager role");
    }
  };

  /** Save the multi-picker selection. Diffs against current managers,
   *  fires a PUT for each admin whose flag changed, then reloads. */
  const saveManagers = async (selectedIds) => {
    const wanted = new Set(selectedIds);
    const changes = managers
      .filter((a) => Boolean(a.is_member_manager) !== wanted.has(a.id))
      .map((a) => ({ id: a.id, is_member_manager: wanted.has(a.id) }));
    if (!changes.length) {
      toast.info("No changes");
      return;
    }
    // Optimistic bulk update.
    setManagers((prev) => prev.map((a) => wanted.has(a.id) ? { ...a, is_member_manager: true } : { ...a, is_member_manager: false }));
    try {
      await Promise.all(changes.map((c) => api.put(`/admin/member-managers/${c.id}`, { is_member_manager: c.is_member_manager })));
      toast.success(`Updated ${changes.length} admin${changes.length === 1 ? "" : "s"}`);
      onDataChange?.();
    } catch (e) {
      toast.error("Could not save every change — please refresh");
      await load();
    }
  };

  /** Apply club-role changes to a user by id; reload summary on success. */
  const applyRoles = async (userId, patch, successMsg) => {
    // Find the user's current roles so we send the FULL desired state.
    const u = members.find((m) => m.id === userId) || {};
    const body = {
      is_general_committee: patch.is_general_committee ?? !!u.is_general_committee,
      executive_role: patch.executive_role !== undefined ? patch.executive_role : (u.executive_role || null),
      coaching_roles: patch.coaching_roles !== undefined ? patch.coaching_roles : (u.coaching_roles || []),
    };
    try {
      await api.put(`/admin/members/${userId}/roles`, body);
      if (successMsg) toast.success(successMsg);
      await load();
      onDataChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not update role");
    }
  };

  const assignExecutive = async (role, user) => {
    // Vacate the role from anyone currently holding it (server also enforces),
    // then set on the chosen user.
    await applyRoles(user.id, { executive_role: role }, `${user.first_name} assigned as ${role}`);
  };

  const clearExecutive = async (role) => {
    const holder = summary?.executive?.[role];
    if (!holder) return;
    await applyRoles(holder.id, { executive_role: null }, `${holder.first_name} removed from ${role}`);
  };

  const saveCommittee = async (newIds) => {
    const current = new Set((summary?.general_committee || []).map((u) => u.id));
    const target = new Set(newIds);
    const toAdd = [...target].filter((id) => !current.has(id));
    const toRemove = [...current].filter((id) => !target.has(id));
    for (const id of toAdd) {
      await applyRoles(id, { is_general_committee: true });
    }
    for (const id of toRemove) {
      await applyRoles(id, { is_general_committee: false });
    }
    toast.success(`Committee updated (${toAdd.length} added, ${toRemove.length} removed)`);
    await load();
  };

  const toggleCoachSlot = async (slot, user, add) => {
    const u = members.find((m) => m.id === user.id);
    const current = new Set(u?.coaching_roles || []);
    if (add) current.add(slot); else current.delete(slot);
    await applyRoles(user.id, { coaching_roles: [...current] },
      add ? `${user.first_name} added as ${slot}` : `${user.first_name} removed from ${slot}`);
  };

  if (loading || !summary) {
    return (
      <div className="space-y-3 mt-4">
        <Card className="p-6 rounded-2xl">
          <div className="h-5 w-40 bg-[hsl(var(--secondary))] animate-pulse rounded mb-3" />
          <div className="h-4 w-72 bg-[hsl(var(--secondary))] animate-pulse rounded" />
        </Card>
      </div>
    );
  }

  const memberOptions = members;

  return (
    <div className="space-y-6 mt-4" data-testid="roles-panel">
      {/* ====== EXECUTIVE ====== */}
      <Card className="p-5 rounded-2xl" data-testid="roles-executive-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] flex items-center justify-center">
              <Crown className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-base font-semibold">Executive</h3>
              <p className="text-xs text-muted-foreground">
                President &amp; Secretary are promoted to Primary Admin. Vice President &amp; Treasurer are promoted to Admin.
              </p>
            </div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {EXEC_ROLES.map((role) => {
            const holder = summary.executive?.[role];
            return (
              <div key={role} className="border border-border rounded-xl p-3 flex items-center justify-between gap-3" data-testid={`roles-exec-${role.replace(/\s+/g, '-').toLowerCase()}`}>
                <div className="min-w-0">
                  <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{role}</div>
                  {holder ? (
                    <div className="mt-1 flex items-center gap-2">
                      <span className="text-sm font-medium truncate">{fullName(holder)}</span>
                      <span className="text-[11px] text-muted-foreground">{holder.member_id}</span>
                    </div>
                  ) : (
                    <div className="mt-1 text-sm text-muted-foreground italic">Vacant</div>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {holder && (
                    <Button variant="ghost" size="sm" onClick={() => clearExecutive(role)} className="h-8 px-2 text-xs">
                      Clear
                    </Button>
                  )}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setExecPicker({ role })}
                    className="h-8 px-3 text-xs rounded-full"
                    data-testid={`roles-exec-assign-${role.replace(/\s+/g, '-').toLowerCase()}`}
                  >
                    {holder ? "Change" : "Assign"}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* ====== GENERAL COMMITTEE ====== */}
      <Card className="p-5 rounded-2xl" data-testid="roles-committee-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] flex items-center justify-center">
              <Shield className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-base font-semibold">General Committee</h3>
              <p className="text-xs text-muted-foreground">
                Committee members are automatically promoted to Admin and added to the General Committee chat room.
              </p>
            </div>
          </div>
          <Button size="sm" onClick={() => setCommPickerOpen(true)} className="rounded-full" data-testid="roles-committee-manage">
            <UserPlus className="h-3.5 w-3.5 mr-1.5" />
            Manage members
          </Button>
        </div>
        {summary.general_committee.length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No general committee members yet.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {summary.general_committee.map((u) => (
              <PersonPill
                key={u.id}
                user={u}
                onRemove={() => applyRoles(u.id, { is_general_committee: false }, `${u.first_name} removed from Committee`)}
                testid={`roles-committee-pill-${u.member_id}`}
              />
            ))}
          </div>
        )}
      </Card>

      {/* ====== COACHES ====== */}
      <Card className="p-5 rounded-2xl" data-testid="roles-coaches-card">
        <div className="flex items-center gap-2 mb-4">
          <div className="h-9 w-9 rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] flex items-center justify-center">
            <Users className="h-4 w-4" />
          </div>
          <div>
            <h3 className="text-base font-semibold">Coaching staff</h3>
            <p className="text-xs text-muted-foreground">
              Assign Head and Assistant Coaches per team. A member can hold multiple coaching positions.
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {COACH_LEVELS.map((level) => (
            <React.Fragment key={level}>
              {COACH_TEAMS.map((team) => {
                const slot = `${level} - ${team}`;
                const holders = summary.coaches?.[slot] || [];
                return (
                  <div key={slot} className="border border-border rounded-xl p-3" data-testid={`roles-coach-${slot.replace(/\s+|\//g, '-').toLowerCase()}`}>
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold">{slot}</div>
                    <div className="mt-2 flex flex-wrap gap-1.5 min-h-[36px]">
                      {holders.length === 0 && <span className="text-xs italic text-muted-foreground">Vacant</span>}
                      {holders.map((u) => (
                        <PersonPill
                          key={u.id}
                          user={u}
                          onRemove={() => toggleCoachSlot(slot, u, false)}
                          testid={`roles-coach-pill-${slot.replace(/\s+|\//g, '-').toLowerCase()}-${u.member_id}`}
                        />
                      ))}
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      className="mt-2 h-8 px-3 text-xs rounded-full w-full"
                      onClick={() => setCoachPicker({ slot })}
                      data-testid={`roles-coach-assign-${slot.replace(/\s+|\//g, '-').toLowerCase()}`}
                    >
                      <UserPlus className="h-3.5 w-3.5 mr-1.5" /> Add
                    </Button>
                  </div>
                );
              })}
            </React.Fragment>
          ))}
        </div>
      </Card>

      {/* ====== MEMBER MANAGERS ====== */}
      <Card className="p-5 rounded-2xl" data-testid="roles-member-managers-card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <div className="h-9 w-9 rounded-xl bg-[hsl(var(--accent))]/15 text-[hsl(var(--accent))] flex items-center justify-center">
              <ShieldCheck className="h-4 w-4" />
            </div>
            <div>
              <h3 className="text-base font-semibold">Member managers</h3>
              <p className="text-xs text-muted-foreground">
                Admins listed here receive the in-app nudge when a new PlayHQ link suggestion appears for an unlinked member. Leave empty to alert every admin.
              </p>
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => setManagerPickerOpen(true)}
            className="rounded-full"
            data-testid="roles-managers-manage"
          >
            <UserPlus className="h-3.5 w-3.5 mr-1.5" />
            Manage managers
          </Button>
        </div>
        {loading ? (
          <p className="text-xs text-muted-foreground italic">Loading admins…</p>
        ) : managers.filter((a) => a.is_member_manager).length === 0 ? (
          <p className="text-xs text-muted-foreground italic">No member managers yet — every admin will receive the alert.</p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {managers.filter((a) => a.is_member_manager).map((a) => {
              // Adapt admin row to the PersonPill shape (which expects
              // ``member_id``). Fall back to username so the pill still
              // renders for admins without a member_id.
              const pillUser = {
                id: a.id,
                first_name: a.first_name,
                last_name: a.last_name,
                member_id: a.username || a.email || "",
                email: a.email,
              };
              return (
                <PersonPill
                  key={a.id}
                  user={pillUser}
                  onRemove={() => toggleManager(a.id, false)}
                  testid={`roles-manager-pill-${a.id}`}
                />
              );
            })}
          </div>
        )}
      </Card>

      {/* Pickers */}
      <MemberPickerDialog
        open={!!execPicker}
        onOpenChange={(v) => !v && setExecPicker(null)}
        members={memberOptions}
        currentUserId={execPicker ? summary.executive?.[execPicker.role]?.id : null}
        title={execPicker ? `Assign ${execPicker.role}` : ""}
        description={execPicker
          ? `Choose the member to hold the role of ${execPicker.role}. Any existing holder will be automatically replaced.`
          : ""}
        onPick={(u) => assignExecutive(execPicker.role, u)}
      />
      <MemberMultiPickerDialog
        open={commPickerOpen}
        onOpenChange={setCommPickerOpen}
        members={memberOptions}
        selectedIds={summary.general_committee.map((u) => u.id)}
        title="General Committee"
        description="Select the members of the General Committee. They will be promoted to Admin and added to the committee chat room."
        onSave={saveCommittee}
      />
      <MemberPickerDialog
        open={!!coachPicker}
        onOpenChange={(v) => !v && setCoachPicker(null)}
        members={memberOptions}
        currentUserId={null}
        title={coachPicker ? `Assign ${coachPicker.slot}` : ""}
        description={coachPicker
          ? `Choose a member to add as ${coachPicker.slot}. The member can hold multiple coaching positions.`
          : ""}
        onPick={(u) => toggleCoachSlot(coachPicker.slot, u, true)}
      />
      <AdminMultiPickerDialog
        open={managerPickerOpen}
        onOpenChange={setManagerPickerOpen}
        admins={managers}
        selectedIds={managers.filter((a) => a.is_member_manager).map((a) => a.id)}
        title="Member managers"
        description="Choose which admins receive the in-app nudge when a new PlayHQ link suggestion appears for an unlinked member."
        onSave={saveManagers}
      />
    </div>
  );
}
